/**
 * Shared diagnostic filtering for sandbox TypeScript checking.
 *
 * Both the frontend (Monaco) and the backend (TypeCheckTool) suppress
 * the same set of diagnostic codes + pattern-based filters. This ensures
 * that code which "passes" on the frontend also passes on the backend.
 */
import ts from 'typescript';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface DiagnosticInfo {
  code: number;
  message: string;
  file?: string;
  line?: number;
  column?: number;
  severity: 'error' | 'warning' | 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns `true` if a diagnostic should be **suppressed** (not shown to
 * the user / not counted as an error).
 *
 * This is the single source of truth for "should we ignore this TS error
 * in the sandbox?" — used identically by the browser and the backend.
 *
 * @param code        The TS diagnostic code (e.g. 2307, 2416, …)
 * @param message     The flattened diagnostic message string
 * @param isFromSandboxFile  Whether the diagnostic originates from a sandbox
 *                    file (vs. a real `.d.ts` from node_modules)
 */
export function shouldSuppressDiagnostic(
  code: number,
  message: string,
  isFromSandboxFile: boolean = true,
): boolean {
  // 1. Diagnostics from non-sandbox files (e.g. real @gotti .d.ts) are noise
  if (!isFromSandboxFile) return true;

  // 2. Codes that are ALWAYS noise regardless of the error message.
  //    These are caused by addExtraLib overlap (2300), cascading
  //    name resolution from @gotti virtual modules (2304), or
  //    overload / enum quirks in sandbox code (2554, 2367, 2678).
  //
  //    NOTE: 2307, 2306, 2305, 2792 are intentionally NOT here. Those
  //    are import-related and must be checked against the message to
  //    distinguish real user errors from harmless @gotti package
  //    resolution failures. See step 3 below.
  //    2305 was moved out because blanket-suppressing it hid real
  //    "Module has no exported member" errors in user code.
  //    2393 was removed because blanket-suppressing it hid real
  //    duplicate method implementations in generated code. The root
  //    cause (addExtraLib overlap) is fixed in monacoUtils instead.
  const ALWAYS_SUPPRESS_CODES = [2300, 2304, 2554, 2367, 2678];
  if (ALWAYS_SUPPRESS_CODES.includes(code)) return true;

  // 3. Pattern-based suppression — message-level filtering for @gotti-specific
  //    issues. This is the key mechanism that lets real user errors through
  //    while suppressing noise from @gotti packages not being in the virtual FS.

  // TS2305: "Module '"<specifier>"' has no exported member '<name>'." TypeScript names the module by the specifier a
  // file imports it through — `'"@gotti/client"'`, `'"../types"'`, `'"@/shared/constants"'` — and once a file imports a
  // module through `@/`, by that name for every miss on it in the file, relative imports included. A miss from the
  // project's own files (relative, `@/` or `file:`) is always reported: a name one file imports from another that does
  // not export it is the test↔code drift the AI's fix loop routes on. A package's miss keeps the pattern this filter has
  // always applied to it (`'[@a-z]` anywhere — on the message TypeScript writes, a lowercase name suppressed and a
  // capitalized one, an invented `Vector2`, reported), because changing it changes what the AI pipeline's type gate
  // reports, which needs a `continueLive` A/B first.
  if (code === 2305 && /^Module '"?(?!file:|@\/)[@a-z]/.test(message) && /'[@a-z]/.test(message)) return true;

  // "Cannot find module '@...' / 'package-name'" — suppress for npm packages
  // (starts with @ or lowercase letter) but NOT for relative paths (starts
  // with . which means the user has a real bad import they need to fix).
  if (/Cannot find module\s+'[@a-z]/.test(message)) return true;

  // "Could not find a declaration file for module" — same family
  if (/Could not find a declaration file/.test(message)) return true;

  // TS2306: "'@gotti/...' is not a module" — cascading from missing @gotti packages.
  // Only suppress when the message refers to an @-scoped or bare npm package.
  if (code === 2306 && /'[@a-z]/.test(message)) return true;

  // TS2792: ESM variant of 2307 — handled by the "Cannot find module" pattern above.

  // TS2416: Override parameter narrowing against @gotti base classes.
  // Sandbox code routinely narrows `string | number` → `string` in overrides.
  if (code === 2416 && /base type '(Server|Client|)System/.test(message)) return true;

  // Enum↔string comparison involving MESSAGES / SYSTEMS constants
  if (/is not comparable to type/.test(message) && /MESSAGES|SYSTEMS/.test(message)) return true;

  // TS2339: Payload envelope property access after override narrowing
  if (code === 2339 && /Payload/.test(message) && /Property '(type|to|data)'/.test(message)) return true;

  // TS2345: Payload union disambiguation
  if (code === 2345 && /Payload/.test(message)) return true;

  return false;
}

/**
 * Given raw TypeScript diagnostics, filter out suppressed ones and
 * return structured `DiagnosticInfo` objects.
 *
 * Parity with the browser (Monaco): diagnostics from non-sandbox files
 * are dropped first (Monaco scopes `getSemanticDiagnostics()` to the
 * current model, so it never sees them). Then the same 2-arg
 * `shouldSuppressDiagnostic(code, msg)` call the frontend uses is
 * applied — no `isFromSandboxFile` override.
 *
 * @param rawDiags     Array of `ts.Diagnostic` from `program.getSyntacticDiagnostics()` + `getSemanticDiagnostics()`
 * @param sandboxFiles Set of file paths that are sandbox files (non-sandbox diagnostics are dropped)
 */
export function filterDiagnostics(
  rawDiags: readonly ts.Diagnostic[],
  sandboxFiles: Set<string>,
): DiagnosticInfo[] {
  return rawDiags
    // Drop diagnostics from non-sandbox files (matches Monaco per-file scoping)
    .filter(d => d.file ? sandboxFiles.has(d.file.fileName) : true)
    // Apply same message-level filter as the browser — 2-arg call, no isFromSandboxFile
    .filter(d => {
      const msg = ts.flattenDiagnosticMessageText(d.messageText, '\n');
      return !shouldSuppressDiagnostic(d.code, msg);
    })
    .map(d => {
      const file = d.file?.fileName ?? '<unknown>';
      const pos = d.file && d.start !== undefined
        ? d.file.getLineAndCharacterOfPosition(d.start)
        : { line: 0, character: 0 };
      return {
        code: d.code,
        message: ts.flattenDiagnosticMessageText(d.messageText, '\n'),
        file,
        line: pos.line + 1,
        column: pos.character + 1,
        severity: d.category === ts.DiagnosticCategory.Error
          ? 'error' as const
          : d.category === ts.DiagnosticCategory.Warning
            ? 'warning' as const
            : 'info' as const,
      };
    });
}
